(() => {
  if (window.PoptexProductContent) return;
  const catalogs = new WeakMap();
  const list = (value) => Array.isArray(value) ? value : [];
  const order = (a, b) => (Number(b.sortOrder) || 0) - (Number(a.sortOrder) || 0) || String(a.id).localeCompare(String(b.id));
  const safeUrl = (value) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try { const url = new URL(value, window.location.href); return ['http:', 'https:'].includes(url.protocol) ? url.href : ''; } catch (_) { return ''; }
  };
  const gallery = (items, fallbackName) => {
    const seen = new Set();
    return list(items).slice().sort(order).flatMap((item) => {
      const url = safeUrl(item.url);
      if (!item.id || !url || seen.has(url)) return [];
      seen.add(url);
      return [{ ...item, url, alt: item.alt || fallbackName || '' }];
    });
  };
  const catalog = (snapshot) => {
    if (snapshot && catalogs.has(snapshot)) return catalogs.get(snapshot);
    const groups = list(snapshot?.productSeriesGroups).filter((group) => group.id && group.status === 'PUBLISHED').slice().sort(order);
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    const stylesById = new Map();
    groups.forEach((group) => list(group.styles).forEach((style) => {
      if (style.id && style.status === 'PUBLISHED' && (!style.groupId || style.groupId === group.id)) stylesById.set(style.id, { ...style, groupId: group.id });
    }));
    list(snapshot?.productSeries).forEach((style) => {
      // Top-level DTOs are authoritative when present, including publication state.
      stylesById.delete(style.id);
      if (style.id && style.status === 'PUBLISHED' && groupsById.has(style.groupId) && (!style.groupStatus || style.groupStatus === 'PUBLISHED')) stylesById.set(style.id, style);
    });
    const styles = [...stylesById.values()].sort(order);
    const templates = window.PoptexTemplateContent?.publishedTemplates(snapshot) || [];
    const templatesById = new Map(templates.map((record) => [record.id, record]));
    const result = { groups, groupsById, styles, stylesById, templates, templatesById };
    if (snapshot) catalogs.set(snapshot, result);
    return result;
  };
  const relatedStyles = (record, data) => (record?.productSeriesId ? [record.productSeriesId] : []).map((id) => data.stylesById.get(id)).filter(Boolean).sort(order);
  const templatePreview = (template) => ({ id: template.id, url: window.PoptexTemplateContent.productPreview(template)?.url || '', alt: template.name || '', caption: template.name || '' });
  const mainGallery = (_style, template) => ({ items: template ? (gallery(template.detailGallery,template.name).length ? gallery(template.detailGallery,template.name) : [templatePreview(template)]) : [] });
  const seriesTemplates = (snapshot, groupId, data = catalog(snapshot)) => {
    if (!data.groupsById.has(groupId)) return [];
    return window.PoptexTemplateContent.eventTemplates(snapshot, 'new')
      .filter((record) => record.templateType === 'PATTERN' && relatedStyles(record, data).some((style) => style.groupId === groupId)).sort(order);
  };
  const templateGallery = (snapshot, groupId, data) => seriesTemplates(snapshot, groupId, data).map(template => ({ id: template.id, url: window.PoptexTemplateContent.displayPreview(template)?.url || '', alt: template.name || '', caption: template.name || '' }));
  const resolve = (snapshot, input = {}) => {
    const data = catalog(snapshot);
    const state = Object.fromEntries(['series', 'style', 'template'].map((key) => [key, input[key] ?? null]));
    const unavailable = (reason = 'invalid') => ({ kind: 'unavailable', reason, state, data });
    if (Object.values(state).some((value) => value !== null && (typeof value !== 'string' || !value.trim()))) return unavailable();
    let group = state.series !== null ? data.groupsById.get(state.series) : null;
    let style = state.style !== null ? data.stylesById.get(state.style) : null;
    let template = state.template !== null ? window.PoptexTemplateContent.getTemplate(snapshot,state.template,state.style) : null;
    const legacyChoices = state.template !== null ? data.templates.filter(p=>p.legacyTemplateId===state.template) : [];
    if (!template && legacyChoices.length > 0 && !state.style) return {kind:'choose',state,data,group,template:null,choices:legacyChoices.map(p=>data.stylesById.get(p.productSeriesId)).filter(Boolean)};

    if ((state.series !== null && !group) || (state.style !== null && !style) || (state.template !== null && !template)) return unavailable();
    if (style && group && style.groupId !== group.id) return unavailable();
    const linked = template ? relatedStyles(template, data) : data.styles;
    if (template && !linked.length) return unavailable('no_product');
    if (style && template && !linked.some((item) => item.id === style.id)) return unavailable();
    if (!style && template && !group) {
      const defaultId = template.productSeriesId;
      if (defaultId) {
        style = linked.find((item) => item.id === defaultId);
        if (!style) return unavailable();
      } else if (linked.length === 1) style = linked[0];
    }
    if (!style) {
      const choices = linked.filter((item) => !group || item.groupId === group.id);
      if (!choices.length) return unavailable(template ? 'no_product' : 'invalid');
      return { kind: 'choose', state, data, group, template, choices };
    }
    group = data.groupsById.get(style.groupId);
    const selected = { series: group.id, style: style.id, template: template?.id || null };
    return {
      kind: 'detail', state: selected, data, group, style, template,
      styles: data.styles.filter((item) => item.groupId === group.id),
      patterns: seriesTemplates(snapshot, group.id, data).filter((record) => relatedStyles(record, data).some((item) => item.id === style.id)),
      gallery: mainGallery(style, template), templateGallery: templateGallery(snapshot, group.id, data), groupGallery: gallery(group.detailGallery, group.name)
    };
  };
  const publicUrl = (rootUrl, state = {}) => {
    const root = new URL(rootUrl || '/', window.location.origin);
    if (!root.pathname.endsWith('/')) root.pathname += '/';
    const url = new URL('pages/product-detail', root);
    ['series', 'style', 'template'].forEach((key) => { if (state[key] != null) url.searchParams.set(key==='template'?'pattern':key, state[key]); });
    return url.pathname + url.search;
  };
  const templateUrl = (rootUrl, id, snapshot) => {
    const data = catalog(snapshot);
    const template = data.templatesById.get(id);
    const style = template && data.stylesById.get(template.productSeriesId);
    if (style) return publicUrl(rootUrl, { series: style.groupId, style: style.id, template: id });
    const result = resolve(snapshot, { template: id });
    return publicUrl(rootUrl, result.kind === 'detail' ? result.state : { template: id });
  };
  window.PoptexProductContent = { catalog, resolve, gallery, mainGallery, templateGallery, safeUrl, publicUrl, templateUrl, relatedStyles };
})();
